import React, { useCallback, useMemo } from "react";
import {
  format,
  getDate,
  getMonth,
  eachWeekOfInterval,
  eachDayOfInterval,
  startOfMonth,
  endOfMonth,
  endOfWeek,
} from "date-fns";
import {
  makeStyles,
  createStyles,
  Typography,
  Button,
} from "@material-ui/core";
import { red, blue } from "@material-ui/core/colors";
import { useHistory } from "react-router-dom";
import { isHoliday } from "@holiday-jp/holiday_jp";

import { get曜日 } from "../../helper/JapanDate";
import { delayPush } from "../../helper/Util";
import Badge from "./Badge";

// CSSスタイルを定義
const useStyles = makeStyles((theme) =>
  createStyles({
    table: {
      width: "100%",
      borderCollapse: "collapse",
    },
    dateButton: {
      paddingRight: 0,
      paddingLeft: 0,
      paddingBottom: theme.spacing(3),
      width: "100%",
      minWidth: "100%",
      height: theme.spacing(10),
      fontSize: 18,
      borderRadius: 0,
    },
    td: {
      padding: 0,
      fontSize: 20,
      border: "solid 1px silver",
      borderCollapse: "collapse",
      width: "calc(100% / 7)",
    },
    dateText: {
      fontSize: 20,
    },
    weekdayText: {
      fontSize: 20,
    },
  })
);

const getCalendarArray = (date) => {
  const sundays = eachWeekOfInterval({
    start: startOfMonth(date),
    end: endOfMonth(date),
  });
  return sundays.map((sunday) =>
    eachDayOfInterval({ start: sunday, end: endOfWeek(sunday) })
  );
};

export default function CalendarBody({ targetDate, data }) {
  const classes = useStyles();
  const todayDate = useMemo(() => new Date(), []);
  const history = useHistory();
  const calendar = useMemo(() => getCalendarArray(targetDate), [targetDate]);
  const weekdays = useMemo(
    () => [
      { text: "日", color: red[700] },
      { text: "月", color: "inherit" },
      { text: "火", color: "inherit" },
      { text: "水", color: "inherit" },
      { text: "木", color: "inherit" },
      { text: "金", color: "inherit" },
      { text: "土", color: blue[700] },
    ],
    []
  );

  const handleClickDateButton = useCallback(
    (date) => {
      delayPush(history, {
        pathname: "/list",
        state: { selectedDate: date },
      });
    },
    [history]
  );

  const getHolidayColor = useCallback((date) => {
    if (isHoliday(date)) {
      return red[700];
    }

    switch (get曜日(date)) {
      case "土":
        return blue[700];
      case "日":
        return red[700];
      default:
        break;
    }

    return "inherit";
  }, []);

  const getBadgeAmount = useCallback(
    (date) => {
      const obj = data.find((obj) => obj.date === format(date, `yyyy-MM-dd`));

      return obj === undefined ? 0 : obj.count;
    },
    [data]
  );

  const getTodayColor = useCallback(
    (date) =>
      format(date, "yyyyMMdd") === format(todayDate, "yyyyMMdd")
        ? "#fcf8e3"
        : "inherit",
    [todayDate]
  );

  const isTargetMonth = useCallback(
    (date) => {
      return getMonth(date) === getMonth(targetDate);
    },
    [targetDate]
  );

  return (
    <table className={classes.table}>
      <thead>
        <tr>
          {/* 曜日表示 */}
          {weekdays.map((weekday, weekdayIdx) => (
            <td
              className={classes.td}
              key={weekdayIdx}
              style={{ color: weekday.color }}
            >
              <Typography className={classes.weekdayText}>
                {weekday.text}
              </Typography>
            </td>
          ))}
        </tr>
      </thead>
      <tbody>
        {/* カレンダー表示 */}
        {calendar.map((weekRow, trIdx) => (
          <tr key={trIdx}>
            {weekRow.map((date, tdIdx) => (
              <td className={classes.td} key={tdIdx}>
                {/* targetDate月の日付のみ表示 */}
                {isTargetMonth(date) ? (
                  <Button
                    variant="text"
                    className={classes.dateButton}
                    style={{ backgroundColor: getTodayColor(date) }}
                    onClick={() => handleClickDateButton(date)}
                  >
                    <span>
                      {/* 日にち表示 */}
                      <Typography
                        className={classes.dateText}
                        style={{
                          color: getHolidayColor(date),
                        }}
                      >
                        {getDate(date)}
                      </Typography>
                      {/* バッヂ表示 */}
                      <Badge amount={getBadgeAmount(date)} />
                    </span>
                  </Button>
                ) : null}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
